import { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Stack,
  Autocomplete,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { getAllSenders } from "services/customerManagement";
import { PricingOptionTypeEnum } from "enums/shared";

const DeliveryPricingForm = ({ initialData, onSubmit, isLoading, showSenderField = false }) => {
  const [formData, setFormData] = useState({
    optionNumber: 0,
    baseKm: 0,
    basePrice: 0,
    incrementStartAfterKm: 0,
    incrementStepKm: 0,
    incrementStepPrice: 0,
    pricingOptionType: PricingOptionTypeEnum.PerDelivery,
    // Pricing and collection mode fields
    pricingRateType: 1, // 1 = Fixed, 2 = Percentage
    deliveryFeeCollectionMode: 1, // 1 = CashByDriver, 2 = OnlineByFanoos
    rateValue: 0,
    roundToNearestHalf: false,
    roundUpHalf: false,
    isActive: false,
    senderIds: [],
  });

  const [senders, setSenders] = useState([]);
  const [loadingSenders, setLoadingSenders] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
        // normalize possible naming differences
        pricingOptionType: initialData.pricingOptionType ?? initialData.PricingOptionType ?? prev.pricingOptionType,
        pricingRateType: initialData.pricingRateType ?? initialData.PricingRateType ?? prev.pricingRateType,
        deliveryFeeCollectionMode: initialData.deliveryFeeCollectionMode ?? initialData.DeliveryFeeCollectionMode ?? prev.deliveryFeeCollectionMode,
        rateValue: initialData.rateValue ?? initialData.RateValue ?? prev.rateValue,
        // map legacy/alternate naming used by backend
        incrementStartAfterKm: initialData.incrementStartAfterKm ?? initialData.incrementStartKm ?? initialData.IncrementStartKm ?? prev.incrementStartAfterKm,
        incrementStepKm: initialData.incrementStepKm ?? initialData.stepKm ?? initialData.StepKm ?? prev.incrementStepKm,
        incrementStepPrice: initialData.incrementStepPrice ?? initialData.incrementAmount ?? initialData.incrementAmount ?? initialData.firstIncrementAmount ?? initialData.FirstIncrementAmount ?? prev.incrementStepPrice,
        baseKm: initialData.baseKm ?? initialData.BaseKm ?? prev.baseKm,
        basePrice: initialData.basePrice ?? initialData.BasePrice ?? prev.basePrice,
      }));
    }
  }, [initialData]);

  useEffect(() => {
    if (showSenderField) {
      fetchSenders();
    }
  }, [showSenderField]);

  const fetchSenders = async () => {
    setLoadingSenders(true);
    try {
      const data = await getAllSenders();
      // The customerManagement service returns an array of sender objects under 'page' or directly as array.
      // Ensure we normalize to an array of objects with `id` and a display name (try common keys).
      console.log("Fetched senders from service:", data);

      const normalized = (data || []).map((s) => ({
        id: s.id || s.senderId || s.Id || s.SenderId,
        fullName: s.fullName || s.fullNameAr || s.fullNameEn || s.name || s.FullName || s.Name,
        raw: s,
      }));

      setSenders(normalized.filter(s => s.id));
    } catch (err) {
      console.error("Failed to load senders", err);
      setSenders([]);
    } finally {
      setLoadingSenders(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Make roundToNearestHalf and roundUpHalf mutually exclusive
    if (name === "roundToNearestHalf" && checked) {
      setFormData((prev) => ({
        ...prev,
        roundToNearestHalf: true,
        roundUpHalf: false,
      }));
    } else if (name === "roundUpHalf" && checked) {
      setFormData((prev) => ({
        ...prev,
        roundUpHalf: true,
        roundToNearestHalf: false,
      }));
    } else {
      let newValue = type === "checkbox" ? checked : value;
      // coerce numeric inputs
      if (type === "number") {
        newValue = value === "" ? "" : Number(value);
      }

      setFormData((prev) => ({
        ...prev,
        [name]: newValue,
      }));
    }
  };

  const handleSendersChange = (event, value) => {
    setFormData((prev) => ({
      ...prev,
      senderIds: value.map(sender => sender.id),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // ensure numeric shape for enums and rate value and include legacy keys
    const incrementStart = Number(formData.incrementStartAfterKm || 0);
    const stepKm = Number(formData.incrementStepKm || 0);
    const incrementAmount = Number(formData.incrementStepPrice || 0);

    const payload = {
      ...formData,
      pricingOptionType: Number(formData.pricingOptionType),
      pricingRateType: Number(formData.pricingRateType),
      deliveryFeeCollectionMode: Number(formData.deliveryFeeCollectionMode),
      rateValue: Number(formData.rateValue),

      // Backward-compatible keys expected by some backend versions
      incrementStartKm: incrementStart,
      IncrementStartKm: incrementStart,
      stepKm: stepKm,
      StepKm: stepKm,
      incrementAmount: incrementAmount,
      IncrementAmount: incrementAmount,
      firstIncrementAmount: incrementAmount,
      FirstIncrementAmount: incrementAmount,

      // ensure base values are numeric and provide PascalCase aliases
      baseKm: Number(formData.baseKm || 0),
      BaseKm: Number(formData.baseKm || 0),
      basePrice: Number(formData.basePrice || 0),
      BasePrice: Number(formData.basePrice || 0),

      // include PascalCase aliases for enums and rate value
      PricingOptionType: Number(formData.pricingOptionType),
      PricingRateType: Number(formData.pricingRateType),
      DeliveryFeeCollectionMode: Number(formData.deliveryFeeCollectionMode),
      RateValue: Number(formData.rateValue),
    };

    onSubmit(payload);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <Stack spacing={2} sx={{ marginTop: "0.5rem" }}>
        <FormControl fullWidth>
          <InputLabel id="pricing-option-type-label">Pricing Option Type</InputLabel>
          <Select
            labelId="pricing-option-type-label"
            id="pricing-option-type"
            name="pricingOptionType"
            value={formData.pricingOptionType}
            label="Pricing Option Type"
            onChange={(e) => setFormData((prev) => ({ ...prev, pricingOptionType: Number(e.target.value) }))}
          >
            <MenuItem value={PricingOptionTypeEnum.PerDelivery}>Per Delivery</MenuItem>
            <MenuItem value={PricingOptionTypeEnum.PerDistance}>Per Distance</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Option Number"
          name="optionNumber"
          type="number"
          value={formData.optionNumber}
          onChange={handleChange}
          required
          fullWidth
        />

        <TextField
          label="Base Km"
          name="baseKm"
          type="number"
          inputProps={{ step: "0.1" }}
          value={formData.baseKm}
          onChange={handleChange}
          fullWidth
        />

        <TextField
          label="Base Price"
          name="basePrice"
          type="number"
          inputProps={{ step: "0.01" }}
          value={formData.basePrice}
          onChange={handleChange}
          fullWidth
        />

        <TextField
          label="Increment Start After Km"
          name="incrementStartAfterKm"
          type="number"
          inputProps={{ step: "0.1" }}
          value={formData.incrementStartAfterKm}
          onChange={handleChange}
          fullWidth
        />

        <TextField
          label="Increment Step Km"
          name="incrementStepKm"
          type="number"
          inputProps={{ step: "0.1" }}
          value={formData.incrementStepKm}
          onChange={handleChange}
          fullWidth
        />

        <TextField
          label="Increment Step Price"
          name="incrementStepPrice"
          type="number"
          inputProps={{ step: "0.01" }}
          value={formData.incrementStepPrice}
          onChange={handleChange}
          fullWidth
        />

        <FormControl fullWidth>
          <InputLabel id="pricing-rate-type-label">Pricing Rate Type</InputLabel>
          <Select
            labelId="pricing-rate-type-label"
            id="pricing-rate-type"
            name="pricingRateType"
            value={formData.pricingRateType}
            label="Pricing Rate Type"
            onChange={(e) => setFormData((prev) => ({ ...prev, pricingRateType: Number(e.target.value) }))}
          >
            <MenuItem value={1}>Fixed (Amount JD)</MenuItem>
            <MenuItem value={2}>Percentage (%)</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel id="delivery-fee-collection-label">Delivery Fee Collection Mode</InputLabel>
          <Select
            labelId="delivery-fee-collection-label"
            id="delivery-fee-collection"
            name="deliveryFeeCollectionMode"
            value={formData.deliveryFeeCollectionMode}
            label="Delivery Fee Collection Mode"
            onChange={(e) => setFormData((prev) => ({ ...prev, deliveryFeeCollectionMode: Number(e.target.value) }))}
          >
            <MenuItem value={1}>Cash By Driver</MenuItem>
            <MenuItem value={2}>Credit by Fanoos</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label={formData.pricingRateType === 1 ? "Amount (JOD)" : "Percentage (%)"}
          name="rateValue"
          type="number"
          inputProps={{ step: formData.pricingRateType === 1 ? "0.01" : "0.1" }}
          value={formData.rateValue}
          onChange={handleChange}
          fullWidth
        />

        {showSenderField && (
          <Autocomplete
            multiple
            options={senders}
            getOptionLabel={(option) => option.fullName || ""}
            value={senders.filter(s => formData.senderIds.includes(s.id))}
            onChange={handleSendersChange}
            loading={loadingSenders}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Business People"
                placeholder="Select senders..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingSenders ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        )}

        <FormControlLabel
          control={
            <Checkbox
              name="roundToNearestHalf"
              checked={formData.roundToNearestHalf}
              onChange={handleChange}
            />
          }
          label="Round To Nearest Half"
        />

        <FormControlLabel
          control={
            <Checkbox
              name="roundUpHalf"
              checked={formData.roundUpHalf}
              onChange={handleChange}
            />
          }
          label="Round Up Half"
        />

        <FormControlLabel
          control={
            <Checkbox
              name="isActive"
              checked={formData.isActive}
              onChange={handleChange}
            />
          }
          label="Is Active"
        />
      </Stack>

      <Button
        type="submit"
        variant="contained"
        sx={{ backgroundColor: "#2A3467", marginTop: "1rem" }}
        disabled={isLoading}
      >
        {isLoading ? "Saving..." : "Save"}
      </Button>
    </Box>
  );
};

export default DeliveryPricingForm;
